import { useEffect, useState } from "react";
import { useI18n } from "../i18n/I18nProvider";

/** Chrome/Edge/Samsung fire this when the site is installable; the event lets us show our own button. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const DISMISS_KEY = "pf.install.dismissedAt";
const DISMISS_DAYS = 30;

function dismissedRecently(): boolean {
  try {
    const at = Number(localStorage.getItem(DISMISS_KEY));
    return Number.isFinite(at) && at > 0 && Date.now() - at < DISMISS_DAYS * 86_400_000;
  } catch {
    return false;
  }
}

function rememberDismiss() {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    // Private mode / blocked storage: the banner just comes back next visit.
  }
}

function isStandalone(): boolean {
  return window.matchMedia?.("(display-mode: standalone)").matches || (navigator as { standalone?: boolean }).standalone === true;
}

/** iOS never fires beforeinstallprompt: installing is manual (Share → Add to Home Screen). */
function isIosSafari(): boolean {
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod/.test(ua) && /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
}

export function InstallPrompt() {
  const { t } = useI18n();
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [ios, setIos] = useState(false);
  const [hidden, setHidden] = useState(() => isStandalone() || dismissedRecently());

  useEffect(() => {
    if (hidden) return;
    const onPrompt = (e: Event) => {
      e.preventDefault(); // keep Chrome's own mini-infobar out; we show ours
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => setHidden(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    setIos(isIosSafari());
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, [hidden]);

  if (hidden || (!deferred && !ios)) return null;

  const dismiss = () => {
    rememberDismiss();
    setHidden(true);
  };
  const install = async () => {
    if (!deferred) return;
    await deferred.prompt();
    const { outcome } = await deferred.userChoice;
    setDeferred(null);
    if (outcome === "accepted") setHidden(true);
    else dismiss();
  };

  return (
    <div className="install-prompt" role="region" aria-label={t("install.title")}>
      <img src="/brand/icon-192.png" alt="" width={40} height={40} />
      <div className="install-prompt-text">
        <strong>{t("install.title")}</strong>
        <span>{deferred ? t("install.body") : t("install.ios")}</span>
      </div>
      {deferred && (
        <button type="button" className="btn btn-primary btn-sm" onClick={install}>
          {t("install.button")}
        </button>
      )}
      <button type="button" className="icon-btn" aria-label={t("nav.close")} onClick={dismiss}>
        ✕
      </button>
    </div>
  );
}
